import type { Slide } from '../../../types';

interface UseSlideNavigationProps {
  currentSlideIndex: number;
  slides: Slide[];
  isAnimating: boolean;
  setIsAnimating: (animating: boolean) => void;
  setCurrentSlideIndex: (index: number) => void;
  loadSlideNodes: (slide: Slide) => Promise<any>;
  flushUpdateQueue: () => Promise<void>;
}

/**
 * Hook for slide navigation
 *
 * Handles:
 * - Next/previous slide navigation
 * - Direct slide navigation
 * - Animation state management
 * - Queue flushing before navigation
 */
export function useSlideNavigation({
  currentSlideIndex,
  slides,
  isAnimating,
  setIsAnimating,
  setCurrentSlideIndex,
  loadSlideNodes,
  flushUpdateQueue,
}: UseSlideNavigationProps) {

  const nextSlide = async () => {
    if (isAnimating || currentSlideIndex >= slides.length - 1) return;

    await flushUpdateQueue();

    setIsAnimating(true);
    const newIndex = currentSlideIndex + 1;
    setCurrentSlideIndex(newIndex);
    await loadSlideNodes(slides[newIndex]);

    setTimeout(() => {
      setIsAnimating(false);
    }, 600);
  };

  const previousSlide = async () => {
    if (isAnimating || currentSlideIndex <= 0) return;

    await flushUpdateQueue();

    setIsAnimating(true);
    const newIndex = currentSlideIndex - 1;
    setCurrentSlideIndex(newIndex);
    await loadSlideNodes(slides[newIndex]);

    setTimeout(() => {
      setIsAnimating(false);
    }, 600);
  };

  const goToSlide = async (index: number) => {
    if (isAnimating || index === currentSlideIndex || index < 0 || index >= slides.length) return;

    await flushUpdateQueue();

    setIsAnimating(true);
    setCurrentSlideIndex(index);
    await loadSlideNodes(slides[index]);

    setTimeout(() => {
      setIsAnimating(false);
    }, 600);
  };

  return {
    nextSlide,
    previousSlide,
    goToSlide,
  };
}
